import React, { useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { openSearch } from '../GlobalSearch';

/**
 * "← back to where I came from" for the Asset Portal (Gordon, 4 November).
 *
 * A screen that opens a device (or one of the portal's own pages) says where
 * it is, in the link's state:
 *
 *   <Link to={`/assets/${id}`} state={cameFrom('Map')}>
 *
 * and the page opened draws "← Map" instead of a fixed "← Assets". Pressing
 * it goes BACK in the browser's history to that screen - the entry it was
 * opened from, however many of the device's tabs were opened since (each tab
 * is a history entry of its own, the ticket page's lesson) - so the map comes
 * back at the same place with the same pin chosen, the list with its filters.
 * Where history cannot be trusted for it (a new tab, or the entry is gone)
 * it opens the screen's address instead. Nothing said (a bookmark, a link in
 * an email): the fallback, "← Assets".
 *
 * From the global search the screen is the search itself: going back reopens
 * it with the same words (`search`).
 */

/** The link state that names this screen. Read at the click, or at the render just before it. */
export function cameFrom(label, extra = {}) {
  if (typeof window === 'undefined') return { from: { label, path: '/assets', ...extra } };
  return {
    from: {
      label,
      path: `${window.location.pathname}${window.location.search}`,
      idx: window.history.state?.idx ?? null,
      ...extra
    }
  };
}

/** Only an address inside this app: history state is not something to trust blindly. */
const usable = (f) => f && typeof f === 'object' && typeof f.label === 'string' && f.label.trim()
  && typeof f.path === 'string' && f.path.startsWith('/') && !f.path.startsWith('//');

/**
 * Where "←" goes on this page. `resetKey` (the device's id) starts a new
 * "where did I come from" when another device opens in the same frame; the
 * one given is kept while the page's own tabs are moved between.
 */
export function useBackTo(fallback = { label: 'Assets', path: '/assets' }, resetKey = null) {
  const location = useLocation();
  const navigate = useNavigate();
  const kept = useRef({ key: resetKey, from: null });
  if (kept.current.key !== resetKey) kept.current = { key: resetKey, from: null };
  // The router's location can lag a render behind the browser's own entry
  // (the React Router transition lesson), so the entry's state is read too.
  const given = location.state?.from;
  const entry = typeof window !== 'undefined' ? window.history.state?.usr?.from : null;
  if (usable(given)) kept.current.from = given;
  else if (!kept.current.from && usable(entry)) kept.current.from = entry;
  const from = kept.current.from;

  const go = (e) => {
    if (!from) return;
    // A new tab or window (Ctrl, Shift, the middle button) opens the address.
    if (e && (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    if (e) e.preventDefault();
    const now = window.history.state?.idx;
    if (Number.isInteger(from.idx) && Number.isInteger(now) && now > from.idx) navigate(from.idx - now);
    else navigate(from.path);
    if (typeof from.search === 'string' && from.search.trim()) {
      const words = from.search;
      setTimeout(() => openSearch(words), 0);
    }
  };
  return {
    from,
    label: from ? from.label : fallback.label,
    path: from ? from.path : fallback.path,
    // To pass on to the page's own tabs, so a reload on one keeps it.
    state: from ? { from } : undefined,
    go
  };
}

/** The "← Label" link itself. */
export default function BackLink({ fallback, resetKey = null, className, testId = 'back-link' }) {
  const back = useBackTo(fallback, resetKey);
  return (
    <Link className={className} to={back.path} onClick={back.go} data-testid={testId}>
      {`← ${back.label}`}
    </Link>
  );
}
